import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { validateAddress, isAddressValid, extractFieldSuggestions } from "modules/google/utils/addressValidation"

const useValidateAddress = (geocoder, form, setCenterLatLng, setMeetingLatLng, setIsButtonDisabled, setButtonLabel) => {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [suggestedAddress, setSuggestedAddress] = useState("")
  const [suggestedComponents, setSuggestedComponents] = useState(null)
  const { t } = useTranslation()

  const match = (component, value) => {
    if (!component || value == null) return false
    const valueLower = String(value).toLowerCase().trim()
    const longName = (component.long_name || "").toLowerCase().trim()
    const shortName = (component.short_name || "").toLowerCase().trim()
    return longName === valueLower || shortName === valueLower
  }

  const getComponent = (addressComponents, types) => {
    const component = addressComponents.find((comp) => types.some((t) => comp.types.includes(t)))
    return component ? { long_name: component.long_name, short_name: component.short_name } : null
  }

  const validateAddressComponents = useCallback((place, values) => {
    const { street_address = "", city = "", state = "", country = "" } = values
    const addressComponents = place.address_components ?? []
    const streetAddressLower = String(street_address || "")
      .toLowerCase()
      .trim()

    const stateComponent = getComponent(addressComponents, ["administrative_area_level_1"])
    const countryComponent = getComponent(addressComponents, ["country"])
    const cityComponent = getComponent(addressComponents, [
      "locality",
      "sublocality",
      "administrative_area_level_1",
      "administrative_area_level_2"
    ])

    const streetNumber = getComponent(addressComponents, ["street_number"])
    const route = getComponent(addressComponents, ["route"])
    const geocodedStreet = [streetNumber?.short_name, route?.short_name].filter(Boolean).join(" ").toLowerCase().trim()

    return {
      isStreetAddress: streetAddressLower && geocodedStreet ? streetAddressLower === geocodedStreet : false,
      isZipValid: true,
      isCityValid: match(cityComponent, city),
      isStateValid: match(stateComponent, state),
      isCountryValid: match(countryComponent, country)
    }
  }, [])

  const handleConfirmAddress = useCallback(async () => {
    setLoading(true)
    setErrorMessage("")
    setSuggestedAddress("")
    setSuggestedComponents(null)
    try {
      const values = await form.validateFields()

      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY
      const validationResult = await validateAddress(values, apiKey)

      if (validationResult.success && isAddressValid(validationResult)) {
        const geocode = validationResult.geocode

        if (geocode?.location) {
          const formatted_address = validationResult.formattedAddress || ""
          setMeetingLatLng(formatted_address)
          setCenterLatLng({
            lat: geocode.location.latitude,
            lng: geocode.location.longitude
          })
          setIsButtonDisabled(true)
          setButtonLabel(t("listings.tabs.location.confirmed"))
          return true
        }
      }

      if (validationResult.success && validationResult.formattedAddress) {
        const fieldSuggestions = extractFieldSuggestions(validationResult)

        if (fieldSuggestions) {
          setSuggestedComponents(fieldSuggestions)
          setSuggestedAddress(validationResult.formattedAddress)
          setErrorMessage(t("listings.tabs.location.validation.address_not_verified"))
          setCenterLatLng?.(null)
          setMeetingLatLng(null)
          setButtonLabel(t("listings.tabs.location.confirm_address"))
          return false
        }
      }

      const userAddress = Object.values(values).join(", ")
      const result = await Promise.race([
        new Promise((resolve) => {
          try {
            geocoder({ address: userAddress }, (place) => {
              try {
                if (place) {
                  const validationResults = validateAddressComponents(place, values)
                  const isValid = Object.values(validationResults).every(Boolean)

                  if (isValid) {
                    const location = place.geometry.location
                    const formatted_address = place.formatted_address ?? ""
                    setMeetingLatLng(formatted_address)
                    setCenterLatLng({ lat: location.lat(), lng: location.lng() })
                    setIsButtonDisabled(true)
                    setErrorMessage("")
                    setSuggestedAddress("")
                    setSuggestedComponents(null)
                    resolve(true)
                  } else {
                    setCenterLatLng?.(null)
                    setMeetingLatLng(null)
                    setButtonLabel(t("listings.tabs.location.confirm_address"))
                    setErrorMessage(t("listings.tabs.location.validation.address_not_verified"))
                    setSuggestedAddress(place?.formatted_address ?? "")

                    const addressComponents = place.address_components ?? []
                    const streetNumber = getComponent(addressComponents, ["street_number"])?.short_name || ""
                    const route = getComponent(addressComponents, ["route"])?.short_name || ""
                    const streetAddress = [streetNumber, route].filter(Boolean).join(" ")
                    const city = getComponent(addressComponents, [
                      "locality",
                      "sublocality",
                      "administrative_area_level_1",
                      "administrative_area_level_2"
                    ])
                    const state = getComponent(addressComponents, ["administrative_area_level_1"])
                    const country = getComponent(addressComponents, ["country"])
                    const zip = getComponent(addressComponents, ["postal_code"])

                    setSuggestedComponents({
                      street_address: streetAddress,
                      city: city?.short_name || city?.long_name || "",
                      state: state?.short_name || state?.long_name || "",
                      country: country?.short_name || country?.long_name || "",
                      zip_code: zip?.short_name || zip?.long_name || ""
                    })
                    resolve(false)
                  }
                } else {
                  setCenterLatLng?.(null)
                  setMeetingLatLng(null)
                  setButtonLabel(t("listings.tabs.location.confirm_address"))
                  setErrorMessage(t("listings.tabs.location.validation.address_not_verified"))
                  resolve(false)
                }
              } catch (innerErr) {
                console.error(innerErr)
                setCenterLatLng?.(null)
                setMeetingLatLng(null)
                setButtonLabel(t("listings.tabs.location.confirm_address"))
                resolve(false)
              }
            })
          } catch (outerErr) {
            console.error(outerErr)
            setCenterLatLng?.(null)
            setMeetingLatLng(null)
            setButtonLabel(t("listings.tabs.location.confirm_address"))
            resolve(false)
          }
        }),
        new Promise((resolve) => setTimeout(() => resolve(false), 10000))
      ])
      return result
    } catch (err) {
      console.error(err)
      return false
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 1000)
    }
  }, [form, geocoder, validateAddressComponents, setCenterLatLng, setMeetingLatLng, setIsButtonDisabled, setButtonLabel, t])

  const clearError = useCallback(() => {
    setErrorMessage("")
    setSuggestedAddress("")
    setSuggestedComponents(null)
  }, [])

  return { handleConfirmAddress, loading, errorMessage, suggestedAddress, suggestedComponents, clearError }
}

export default useValidateAddress
